import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, computed, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ZnMultiSelect, ZnSingleSelect, ZnSelectQueryParams, ZendraLocalizationServices } from '@zendra/shared';
import { LidarProjectionChangesDto } from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/lidar-inventory/models';
import { Observable, finalize, startWith, switchMap } from 'rxjs';
import { Catalog } from '../../inventory-data-entry/services/catalog';

interface UndoRoutineService {
  executeUndoLidarInventoryRoutine(input: { meditionNumber: number; standNumbers: string[] }): Observable<LidarProjectionChangesDto>;
}

export interface UndoRoutineSelection {
  meditionNumber: number;
  standNumbers: string[];
}

@Component({
  selector: 'lib-action-undo-routine',
  imports: [CommonModule, LocalizationPipe, ReactiveFormsModule, ZnSingleSelect, ZnMultiSelect],
  templateUrl: './action-undo-routine.html',
})
export class ActionUndoRoutine {
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly toaster = inject(ToasterService);

  @Input({ required: true }) deleteService!: UndoRoutineService;
  @Input({ required: true }) catalogService!: Catalog;
  @Input({ required: true }) waitForProjection!: (changes: LidarProjectionChangesDto) => Observable<unknown>;

  readonly completed = output<void>();
  readonly loading = signal(false);
  readonly standOptions = signal<{ key: string; value: string }[]>([]);
  readonly localizationResource = ZendraLocalizationServices.Inventory;
  readonly languageLocalizationResource = ZendraLocalizationServices.LanguageService;

  readonly form = this.formBuilder.group({
    meditionNumber: [null as number | null, Validators.required],
    standNumbers: [[] as string[]],
  });

  private readonly status = toSignal(this.form.statusChanges.pipe(startWith(this.form.status)), { initialValue: this.form.status });
  readonly canSave = computed(() =>
    this.status() === 'VALID' && this.standOptions().length > 0 && !this.loading()
  );

  constructor() {
    this.form.controls.meditionNumber.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(meditionNumber => this.loadStands(meditionNumber));
  }

  readonly meditionNumbers = (params?: ZnSelectQueryParams) => {
    this.catalogService.useLidar = true;
    return this.catalogService.loadMeditionNumbers(params);
  };

  confirm(): void {
    if (!this.canSave()) return;
    const value = this.form.getRawValue();
    const selection: UndoRoutineSelection = {
      meditionNumber: Number(value.meditionNumber),
      standNumbers: value.standNumbers ?? [],
    };
    this.loading.set(true);
    this.deleteService.executeUndoLidarInventoryRoutine(selection).pipe(
      switchMap(changes => this.waitForProjection(changes)),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.loading.set(false)),
    ).subscribe({
      next: () => {
        this.toaster.success('Inventory::Actions:UndoLidarInventorySuccess');
        this.completed.emit();
        this.activeModal.close(selection);
      },
      error: error => console.error(error),
    });
  }

  dismiss(): void { if (!this.loading()) this.activeModal.dismiss(); }

  private loadStands(meditionNumber: number | null): void {
    this.standOptions.set([]);
    this.form.controls.standNumbers.setValue([]);
    if (!meditionNumber) return;
    this.catalogService.loadLidarStands(meditionNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(options => {
        this.standOptions.set(options);
        this.form.controls.standNumbers.setValue([]);
        this.form.controls.standNumbers.markAsTouched();
      });
  }
}
